import Cookies from 'universal-cookie';
import openpathConfig from 'openpathConfig';

import { REGIONAL_PROD_SUBDOMAINS } from 'constants/envs';

import { getAltaDomain, getEnvironment } from './domains';
import { getWindowLocation } from './window';

// Regional prod suffixes match the URL subdomain (prodeu1 -> 'eu'), so they are
// spread from the shared map rather than restated. The annotation stays
// exhaustive: a new OP.Env is a compile error here, not a silent '' suffix.
const TOKEN_SUFFIX: Record<OP.Env, string> = {
  ...REGIONAL_PROD_SUBDOMAINS,
  sandbox: 'sandbox',
  dev: 'dev',
  prod: '', // intentionally left blank
  test: 'test',
  mine: '', // intentionally left blank
};

/**
 *
 * @param env the environment to get the token suffix for
 * @description Returns the token suffix for the given environment to match how getAccessToken
 * expects the cookie to be named.
 * @returns
 */
export const getTokenSuffix = (env: OP.Env) => {
  return TOKEN_SUFFIX[env] || '';
};

/**
 * @description Sets the access token cookie. For alta.avigilon domains, sets
 * cookies at root domain for cross-region auth. For localhost uses legacy
 * cookie settings.
 *
 * @param token The access token to set
 * @param env The environment to use for cookie naming (e.g. 'prodeu1', 'dev').
 * If provided, uses 'accessToken-{env}'. If not provided on alta.avigilon
 * domain, detects from URL. Otherwise defaults to 'accessToken'.
 */
export const setAccessToken = (token?: string | null, env?: OP.Env) => {
  const cookies = new Cookies();
  const domain = getAltaDomain();

  if (domain) {
    // Use explicitly provided env for mapping to a suffix if available, otherwise try detected env
    const tokenSuffix = env
      ? (getTokenSuffix(env) ?? getEnvironment())
      : getEnvironment();

    const accessTokenName = tokenSuffix
      ? `accessToken-${tokenSuffix}`
      : 'accessToken';

    // Always set at root domain for cross-region auth
    cookies.set(accessTokenName, token, {
      ...openpathConfig.COOKIE_SETTINGS,
      domain: '.alta.avigilon.com',
    });
  } else {
    // Handle localhost. We could potentially still allow suffixes if we find its helpful
    cookies.set('accessToken', token, openpathConfig.COOKIE_SETTINGS);
  }
};

/**
 * @description Gets the access token. For alta.avigilon domains, uses the
 * environment-specific name (dev, prod, sandbox, prodeu1). Otherwise uses
 * default name.
 *
 * @returns access token for the environment/domain.
 */
export const getAccessToken = (envOverride?: string) => {
  const cookies = new Cookies();
  const domain = getAltaDomain();

  const env = envOverride || getEnvironment();
  return domain && env
    ? cookies.get(`accessToken-${env}`)
    : cookies.get('accessToken');
};

/**
 * Temporary function to remove legacy access tokens
 * that may have been set on the wrong domain since moving all
 * tokens to .alta.avigilon.com
 */
export const removeLegacyAccessToken = () => {
  const cookies = new Cookies();
  const domain = getAltaDomain();

  if (domain) {
    const env = getEnvironment();
    const accessTokenName = env ? `accessToken-${env}` : 'accessToken';
    cookies.remove(accessTokenName, {
      path: '/',
      domain,
    });
  }
};

/**
 * Temporary function to remove access tokens set by our cloudfront function
 * that may have been set when app switching from Aware to Access
 * which sets on the host of the domain
 */
export const removeCloudfrontSetAccessToken = () => {
  const cookies = new Cookies();
  const domain = getAltaDomain();

  if (domain) {
    const env = getEnvironment();
    const accessTokenName = env ? `accessToken-${env}` : 'accessToken';

    cookies.remove(accessTokenName, {
      path: '/',
      domain: getWindowLocation().hostname,
    });
  }
};

/**
 * @description Removes the access token. For alta.avigilon domains, uses the
 * environment-specific name (dev, prod, sandbox, prodeu1). Otherwise uses
 * default name.
 */
export const removeAccessToken = () => {
  const cookies = new Cookies();
  const domain = getAltaDomain();

  if (domain) {
    const env = getEnvironment();
    const accessTokenName = env ? `accessToken-${env}` : 'accessToken';
    cookies.remove(accessTokenName, {
      path: '/',
      domain: '.alta.avigilon.com',
    });
  }

  /**
   * We call this secondary removal as a temporary "fix" for the following scenario as well as for control.openpath.com.
   * 1. Log into control.alta.avigilon.com (sets cookie to .alta.avigilon.com)
   * 2. App switch to Aware and app switch back (a cookie is set on the more specific control.alta.avigilon.com set from our cloudfront function we didn't update when this change was made for native apps like Mailroom to share cookies)
   *
   * The issue is when app switching back from Aware and then logging out from Access
   * you get stuck in an infinite loop because theres still the control.alta.avigilon.com
   * domain token sitting around and our app is trying to use it to "log in" and creates an
   * infinite rerender loop. We could try and fix the cloudfront function but this is a slightly
   * quicker and dirty qay to achieve the same thing. That function should potentially be obsolete
   * soon anyways once we permanently move to alta.avigilon.com
   */
  cookies.remove('accessToken', { path: '/' });

  removeLegacyAccessToken();
  removeCloudfrontSetAccessToken();
};
